import {
  AHText,
  Box,
  RoutineOccasionStatusCircleRelay,
  TouchableBox,
  useTheme,
  useTime,
} from "arkh";
import { DateTime, Duration } from "luxon";
import React, { useCallback, useMemo } from "react";
import { StyleProp, StyleSheet, ViewStyle } from "react-native";
import { graphql, useFragment } from "react-relay";
import { useRoutineOverviewListItemHeight } from "./useRoutineOverviewListItemHeight";

import { WidgetRoutineOverviewListItemExpireText_routineOccasion$key } from "./__generated__/WidgetRoutineOverviewListItemExpireText_routineOccasion.graphql";
import { WidgetRoutineOverviewListItem_routine$key } from "./__generated__/WidgetRoutineOverviewListItem_routine.graphql";
import {
  RoutineOccasionStatus,
  WidgetRoutineOverviewListItem_routineOccasion$key,
} from "./__generated__/WidgetRoutineOverviewListItem_routineOccasion.graphql";

type RoutineTextForStatusArgs = {
  status?: RoutineOccasionStatus | undefined;
  startDate: string | null;
  expirationDate: string | null;
};

type RoutineOverviewProps = {
  routine: WidgetRoutineOverviewListItem_routine$key;
  routineOccasion: WidgetRoutineOverviewListItem_routineOccasion$key;
  onPress?: (
    routineId: string,
    sequenceNumber: number,
    teamId?: string
  ) => void;
  style?: StyleProp<ViewStyle>;
};

const { detailStyle, expireWrapper, teamNameStyle, contentStyle, body } =
  StyleSheet.create({
    detailStyle: {
      flexShrink: 0,
      flexGrow: 0,
      flexWrap: "nowrap",
      overflow: "hidden",
    },
    expireWrapper: {
      flex: 1,
      justifyContent: "flex-end",
      alignItems: "center",
      flexDirection: "row",
    },
    teamNameStyle: {
      flexShrink: 0,
      flexGrow: 0,
      flexWrap: "nowrap",
    },
    wrapperStyle: {},
    contentStyle: {
      zIndex: 3,
      borderBottomColor: "#eee",
      borderBottomWidth: StyleSheet.hairlineWidth,
      flexDirection: "row",
      justifyContent: "space-between",
      alignContent: "center",
      alignItems: "center",
    } as const,
    body: {
      alignItems: "flex-start",
      flex: 1,
      justifyContent: "center",
    },
  } as const);

function RoutineOverviewCmp(props: RoutineOverviewProps) {
  const routine = useFragment(
    graphql`
      fragment WidgetRoutineOverviewListItem_routine on Routine {
        id
        title
        ownerTeam {
          id
          name
        }
      }
    `,
    props.routine
  );

  const routineOccasion = useFragment(
    graphql`
      fragment WidgetRoutineOverviewListItem_routineOccasion on RoutineOccasion {
        id
        status(calculateNoTasks: true, calculateNotCompletedStarted: true)
        sequenceNumber
        ...WidgetRoutineOverviewListItemExpireText_routineOccasion
        ...RoutineOccasionStatusCircle_RoutineOccasion
      }
    `,
    props.routineOccasion
  );
  const { status, sequenceNumber } = routineOccasion;

  const routineId = routine.id;
  const teamId = routine.ownerTeam?.id;

  const _onPress = props.onPress;
  const onPressItem = useCallback(
    () => _onPress?.(routineId, sequenceNumber, teamId),
    [_onPress, routineId, sequenceNumber, teamId]
  );

  const theme = useTheme();

  const titleStyle = useMemo(
    () => ({
      color:
        status === "IN_PROGRESS_EXPIRED" ? theme.negative : theme.darkerText,
    }),
    [status, theme.darkerText, theme.negative]
  );

  const height = useRoutineOverviewListItemHeight();
  return (
    <TouchableBox
      spaceHorizontal="medium"
      color="surface"
      space="xsmall"
      style={[contentStyle, { height }]}
      onPress={onPressItem}
    >
      <Box spaceVertical="xxsmall" spaceRight="small" style={body}>
        <AHText
          type="title-small"
          adjustsFontSizeToFit
          minimumFontScale={0.6}
          style={titleStyle}
          numberOfLines={1}
        >
          {routine.title}
        </AHText>
        <AHText type="detail" style={teamNameStyle}>
          {routine.ownerTeam?.name ?? ""}
        </AHText>
      </Box>
      <Box spaceLeft="small" style={expireWrapper}>
        <ExpireText routineOccasion={routineOccasion} />
        <RoutineOccasionStatusCircleRelay occasion={routineOccasion} />
      </Box>
    </TouchableBox>
  );
}

export const useToRelative = () => {
  const toRelative = useCallback((dateStr: string, now: DateTime) => {
    const date = DateTime.fromISO(dateStr);
    const lessThanAMinute = Math.abs(date.diff(now).as("minutes")) < 1;
    const lessThanADay = Math.abs(date.diff(now).as("minutes")) < 60 * 24;
    if (lessThanAMinute) {
      return "precis nu";
    }

    if (lessThanADay) {
      return date.setLocale("sv-SE").toRelative({ style: "short" }) ?? "";
    }
    return date.setLocale("sv-SE").toRelativeCalendar() ?? "";
  }, []);
  return toRelative;
};

const useRoutineTextForStatus = (
  { status, startDate, expirationDate }: RoutineTextForStatusArgs,
  now: DateTime
): { label: string; date: string } => {
  const toRelative = useToRelative();

  return useMemo(() => {
    switch (status) {
      case "COMPLETED":
        return {
          label: "",
          date: "",
        };
      case "REQUIRED_COMPLETED":
        return {
          label: "",
          date: "",
        };

      case "IN_PROGRESS":
        return {
          label: "Deadline",
          date: expirationDate ? toRelative(expirationDate, now) : "",
        };
      case "IN_PROGRESS_EXPIRED":
        return {
          label: "Deadline",
          date: expirationDate ? toRelative(expirationDate, now) : "",
        };
      case "FUTURE":
        return {
          label: "Börjar",
          date: startDate ? toRelative(startDate, now) : "",
        };
      default:
        return {
          label: "",
          date: "",
        };
    }
  }, [expirationDate, now, startDate, status, toRelative]);
};

function ExpireText(props: {
  routineOccasion: WidgetRoutineOverviewListItemExpireText_routineOccasion$key;
}) {
  const now = useTime(Duration.fromObject({ seconds: 60 }).as("milliseconds"));
  const { startAt, expireAt, status } = useFragment(
    graphql`
      fragment WidgetRoutineOverviewListItemExpireText_routineOccasion on RoutineOccasion {
        startAt
        expireAt
        status(calculateNoTasks: true, calculateNotCompletedStarted: true)
      }
    `,
    props.routineOccasion
  );

  const expireInfo = useRoutineTextForStatus(
    {
      status,
      expirationDate: expireAt,
      startDate: startAt,
    },
    now
  );

  const expireStr = useMemo(
    () => expireInfo.label + " " + expireInfo.date,
    [expireInfo]
  );
  return (
    <AHText type="detail" style={detailStyle} spaceRight="small">
      {expireStr}
    </AHText>
  );
}
export const RoutineOverviewListItem = React.memo(RoutineOverviewCmp);
